import React, { useEffect, useRef, useState } from 'react';
import '../merchants/components/CreateMerchant.css';
import './Developers.css';
import '../transaction-monitoring/TransactionMonitoring.css';
import {
  BsLink45Deg, BsLockFill, BsKeyFill, BsEye, BsEyeSlash,
  BsClipboard, BsClipboardCheck, BsArrowRepeat, BsCheckCircleFill, BsExclamationCircle,
  BsImage,
} from 'react-icons/bs';
import { ListConsolePage } from '../../components/shared/ListConsoleLayout';
import Loader from '../../components/shared/Loader';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../store/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import {
  fetchMerchantDeveloperSettings,
  updateMerchantDeveloperSettings,
} from './api/api';
import type { CopyField } from './models/types';
import {
  buildDeveloperUpdatePayload,
  generateApiKey,
  generateSecretKey,
  isValidUrl,
  parseDeveloperSettings,
} from './utils/utils';

const Developers: React.FC = () => {
  const authUser = useAuthStore((state) => state.user);
  const merchantId = authUser?.merchantId;
  const { canUpdate } = usePermissions();
  // "Manage Keys" capability — gates generating/rotating API keys and saving webhook settings.
  const canManageKeys = canUpdate('MERCH-DEVELOPERS');

  const [webhookUrl, setWebhookUrl] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [copiedField, setCopiedField] = useState<CopyField | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const merchantRecordRef = useRef<Record<string, unknown>>({});

  const applyMerchantRecord = (data: Record<string, unknown>) => {
    merchantRecordRef.current = data;
    const settings = parseDeveloperSettings(data);
    setWebhookUrl(settings.webhookUrl);
    setRedirectUrl(settings.redirectUrl);
    setLogoUrl(settings.logoUrl);
    setApiKey(settings.apiKey);
    setSecretKey(settings.secretKey);
  };

  useEffect(() => {
    if (!merchantId) return;

    setLoading(true);
    setFetchError(null);

    fetchMerchantDeveloperSettings(merchantId)
      .then(applyMerchantRecord)
      .catch((err: unknown) => {
        setFetchError(
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (err as Error)?.message ||
          'Unable to load developer settings.',
        );
      })
      .finally(() => setLoading(false));
  }, [merchantId]);

  const hasKeys = Boolean(apiKey && secretKey);

  const handleGenerateKeys = () => {
    setApiKey(generateApiKey());
    setSecretKey(generateSecretKey());
    setShowSecretKey(false);
    setSaved(false);
  };

  const handleCopy = async (field: CopyField, value: string) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField((prev) => (prev === field ? null : prev)), 1600);
    } catch {
      // Clipboard permission denied or unavailable.
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!merchantId) return;

    const nextErrors: Record<string, string> = {};
    if (!isValidUrl(webhookUrl)) nextErrors.webhookUrl = 'Enter a valid http(s) URL';
    if (!isValidUrl(redirectUrl)) nextErrors.redirectUrl = 'Enter a valid http(s) URL';
    if (!isValidUrl(logoUrl)) nextErrors.logoUrl = 'Enter a valid http(s) URL';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setSaved(false);
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaved(false);

    try {
      const payload = buildDeveloperUpdatePayload(
        Number(merchantId),
        merchantRecordRef.current,
        { webhookUrl, redirectUrl, logoUrl, apiKey, secretKey },
      );
      const data = await updateMerchantDeveloperSettings(payload);
      if (data) applyMerchantRecord(data);
      setSaved(true);
    } catch (err: unknown) {
      setSaveError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Unable to save developer settings.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ListConsolePage
      fill={false}
      title="Developers"
      // subtitle="Manage your webhook, redirect URL, and API credentials"
      error={fetchError && !loading ? fetchError : null}
    >
      {!merchantId ? (
        <div className="cm-card console-body-card">
          <p className="cm-field-hint">
            Developer settings are managed per merchant account. Sign in as (or impersonate) a
            merchant to configure these.
          </p>
        </div>
      ) : loading ? (
        <div className="cm-card console-body-card">
          <Loader size="md" label="Loading developer settings…" />
        </div>
      ) : (
        <div className="cm-card console-body-card">
          <form onSubmit={handleSubmit}>
            <div className="cm-step-body">
              <div className="cm-section-block">
                <div className="cm-section-head">
                  <h4 className="cm-section-title">Endpoint Configuration</h4>
                  <span className="cm-section-hint">Callback and redirect endpoints</span>
                </div>
                <div className="cm-grid">
                  <label className="cm-label">
                    <span>Webhook URL</span>
                    <div className="relative">
                      <BsLink45Deg className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="url"
                        className={cn('pl-9', errors.webhookUrl && 'border-destructive')}
                        value={webhookUrl}
                        onChange={(e) => setWebhookUrl(e.target.value)}
                        placeholder="https://your-domain.com/webhook"
                      />
                    </div>
                    <p className="cm-field-hint">We'll POST payment status updates to this URL.</p>
                    {errors.webhookUrl && <p className="cm-err">{errors.webhookUrl}</p>}
                  </label>
                  <label className="cm-label">
                    <span>Redirect URL</span>
                    <div className="relative">
                      <BsLink45Deg className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="url"
                        className={cn('pl-9', errors.redirectUrl && 'border-destructive')}
                        value={redirectUrl}
                        onChange={(e) => setRedirectUrl(e.target.value)}
                        placeholder="https://your-domain.com/redirect"
                      />
                    </div>
                    <p className="cm-field-hint">Customers are sent here after completing checkout.</p>
                    {errors.redirectUrl && <p className="cm-err">{errors.redirectUrl}</p>}
                  </label>
                </div>
              </div>

              <div className="cm-section-block">
                <div className="cm-section-head">
                  <h4 className="cm-section-title">Branding</h4>
                  <span className="cm-section-hint">Logo shown on hosted checkout</span>
                </div>
                <div className="cm-grid">
                  <label className="cm-label cm-span2">
                    <span>Logo URL</span>
                    <div className="relative">
                      <BsImage className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="url"
                        className={cn('pl-9', errors.logoUrl && 'border-destructive')}
                        value={logoUrl}
                        onChange={(e) => setLogoUrl(e.target.value)}
                        placeholder="https://your-domain.com/logo.png"
                      />
                    </div>
                    <p className="cm-field-hint">Public URL to your merchant logo image.</p>
                    {errors.logoUrl && <p className="cm-err">{errors.logoUrl}</p>}
                  </label>
                </div>
              </div>

              <div className="cm-section-block">
                <div className="cm-section-head dev-section-head">
                  <div>
                    <h4 className="cm-section-title">API Credentials</h4>
                    <span className="cm-section-hint">Used to authenticate your integration's API requests</span>
                  </div>
                  {canManageKeys && (
                    <Button type="button" className="dev-generate-btn shrink-0" onClick={handleGenerateKeys}>
                      <BsArrowRepeat /> {hasKeys ? 'Regenerate Keys' : 'Generate Api Keys'}
                    </Button>
                  )}
                </div>

                <div className="cm-grid">
                  <label className="cm-label cm-span2">
                    <span>API Key</span>
                    <div className="relative">
                      <BsKeyFill className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        className="dev-mono pl-9 pr-10"
                        value={apiKey}
                        readOnly
                        placeholder="Not generated yet"
                      />
                      {apiKey && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                          onClick={() => handleCopy('apiKey', apiKey)}
                          aria-label="Copy API key"
                        >
                          {copiedField === 'apiKey' ? <BsClipboardCheck className="dev-copied-icon" /> : <BsClipboard />}
                        </Button>
                      )}
                    </div>
                  </label>
                  <label className="cm-label cm-span2">
                    <span>Secret Key</span>
                    <div className="relative">
                      <BsLockFill className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        className="dev-mono dev-input-2actions pl-9 pr-16"
                        type={showSecretKey ? 'text' : 'password'}
                        value={secretKey}
                        readOnly
                        placeholder="Not generated yet"
                      />
                      {secretKey && (
                        <div className="dev-input-actions">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-full hover:bg-transparent"
                            onClick={() => setShowSecretKey((v) => !v)}
                            aria-label={showSecretKey ? 'Hide secret key' : 'Show secret key'}
                          >
                            {showSecretKey ? <BsEyeSlash /> : <BsEye />}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-full hover:bg-transparent"
                            onClick={() => handleCopy('secretKey', secretKey)}
                            aria-label="Copy secret key"
                          >
                            {copiedField === 'secretKey' ? <BsClipboardCheck className="dev-copied-icon" /> : <BsClipboard />}
                          </Button>
                        </div>
                      )}
                    </div>
                  </label>
                </div>

                {hasKeys && (
                  <p className="cm-field-hint dev-warning-hint">
                    <BsExclamationCircle /> Keep your secret key private. Regenerating replaces both values immediately and invalidates the old ones.
                  </p>
                )}
              </div>

              {saveError && <div className="cm-api-err">{saveError}</div>}

              {saved && (
                <div className="dev-success-banner">
                  <BsCheckCircleFill /> Developer settings saved
                </div>
              )}

              <div className="cm-nav">
                <div className="flex-1" />
                {canManageKeys && (
                  <Button type="submit" disabled={saving}>
                    {saving ? 'Saving…' : 'Submit'}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
      )}
    </ListConsolePage>
  );
};

export default Developers;
